import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useNavContext } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/useNavContext'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import Sites from '@toasttab/shared/src/data/Sites.json'
import {
  formatRegionBannerUrl,
  getRegion,
  updateRegionBannerState
} from '@toasttab/shared/src/utils/bannerUtils'
import { getStringValue } from '@toasttab/shared/src/utils/stringUtils'
import useWindowSize from '@toasttab/shared/src/hooks/useWindowSize'
import MobileRegionMenu from './MobileRegionMenu'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'

const localizeRegionLinks = ({ localizedEntries, packageObj, sites }: any) =>
  sites.map((site: any) => {
    const entry = localizedEntries?.find(
      (x: any) => x.language === site.language
    )
    let slug = ''
    if (entry?.slug || entry?.slugOverride) {
      if (entry.slugOverride) {
        slug = entry.slugOverride.startsWith('/')
          ? entry.slugOverride
          : '/' + entry.slugOverride
      } else {
        slug = entry.slug.startsWith('/') ? entry.slug : '/' + entry.slug
      }
    }
    const entryPath = { uri: slug }
    const url = formatRegionBannerUrl(site, packageObj, entryPath) || '/'

    return { language: site.languageName, url }
  })

const NavRegionSwitcher = ({ isUtilityNav }: { isUtilityNav?: boolean }) => {
  const { t } = useI18next()
  const {
    heapClasses,
    isMobileNavOpen,
    isOverlayOpen,
    isRegionMenuOpen,
    localizedEntries,
    navigationData,
    setIsOverlayOpen,
    setIsRegionMenuOpen,
    setSelectedParentMenu,
    showRegionSelector,
    siteId,
    siteRegion
  } = useNavContext()
  const [regionData, setRegionData] = useState({
    countryCode: '',
    regionPreference: ''
  })
  const [isRegionSelectorActive, setIsRegionSelectorActive] = useState(false)

  const { packageName } = usePageContext()
  const { geoData, loading } = useGeoLocation()
  const windowSize = useWindowSize()

  const isDesktopLayout = windowSize.width >= 1024

  const activeSiteBanner = Sites.sites.filter((site) => site.siteId === siteId)
  const packageObj = Sites.packages.find((el) => el.package === packageName)

  useEffect(() => {
    if (geoData) {
      setRegionData({
        countryCode: geoData.country.iso_code,
        regionPreference: geoData.regionPreference.iso_code
      })
    }
  }, [geoData, loading])

  useEffect(() => {
    if (regionData.countryCode) {
      const activeBannerCountryCode: string = activeSiteBanner[0].countryCode
        .toString()
        .toLowerCase()
      const preRegion: string = getStringValue(regionData.regionPreference)
      const country: string = getStringValue(regionData.countryCode)

      const bannerstate = updateRegionBannerState(
        activeBannerCountryCode,
        preRegion,
        country
      )
      setIsRegionSelectorActive(bannerstate)
    } else {
      setIsRegionSelectorActive(true)
    }
  }, [activeSiteBanner, regionData])

  const handleAfterSubmitting = (submitedRegion: any) => {
    const parseRegion: Array<string> = submitedRegion.split(/[\/\/]/)
    const region: string = getRegion(parseRegion[1])
    if (region) {
      _helpers.createCookie(`_preferredRegion`, `${region}`, 30, '/')
      setRegionData({
        countryCode: regionData.countryCode,
        regionPreference: region
      })
      window.location.assign(submitedRegion)
    }
  }

  const regionButtonClasses = classNames(
    heapClasses.regionSwitcher,
    'font-effra text-blue-75 font-semibold lg:text-default lg:font-normal whitespace-nowrap pb-4 font-normal before:content-icon-nav-internet before:relative',
    {
      hidden: !isMobileNavOpen && navigationData?.menus?.length,
      'before:inline-block before:scale-75 before:h-4 before:w-4 before:-top-1 before:mr-2 text-[12px] lg:flex items-center -mb-4':
        isUtilityNav,
      'mr-4 lg:mr-6 text-[15px] lg:block before:top-[6px] before:mr-1 -mb-2':
        !isUtilityNav
    }
  )
  const regionMenuClasses = classNames(
    'lg:fixed z-20 text-left font-effra bg-white rounded-lg w-[249px] p-4 pt-6 -ml-12 xl:-ml-4 mt-2 shadow-floating',
    {
      hidden: !isRegionMenuOpen || !isDesktopLayout,
      block: isRegionMenuOpen
    }
  )

  useEffect(() => {
    const handleEsc = (event: any) => {
      if (
        event.key === 'Escape' ||
        event.key === 'ESC' ||
        event.keyCode === 27
      ) {
        setIsOverlayOpen(false)
        setIsRegionMenuOpen(false)
      }
    }

    window.addEventListener('keydown', handleEsc)
  }, [setIsOverlayOpen, setIsRegionMenuOpen])

  const handleOpen = () => {
    setSelectedParentMenu(null)
    setIsRegionMenuOpen(true)
    setIsOverlayOpen(true)
  }
  const handleClose = () => {
    setSelectedParentMenu(null)
    setIsRegionMenuOpen(false)
    setIsOverlayOpen(false)
  }
  const toggleMenu = () => {
    setSelectedParentMenu(null)
    setIsRegionMenuOpen(!isRegionMenuOpen)
    setIsOverlayOpen(!isOverlayOpen)
  }

  const sitesByContinent = Sites.sites.reduce((acc: any, curr: any) => {
    if (acc[curr.continent]) {
      acc[curr.continent].push(curr)
    } else {
      acc[curr.continent] = [curr]
    }
    return acc
  }, {})

  return showRegionSelector && isRegionSelectorActive ? (
    <div
      className='relative'
      data-testid='NavRegionSwitcher'
      onMouseLeave={isDesktopLayout ? handleClose : null}
    >
      <button
        aria-expanded={isRegionMenuOpen}
        aria-haspopup={true}
        aria-hidden={!isDesktopLayout && !isMobileNavOpen}
        className={regionButtonClasses}
        onClick={isDesktopLayout ? handleOpen : toggleMenu}
        onMouseEnter={isDesktopLayout ? handleOpen : null}
        aria-controls={
          isDesktopLayout ? 'nav-region-menu-desktop' : 'nav-region-menu-mobile'
        }
      >
        <span className='hidden min-[414px]:inline'>
          {siteRegion.toUpperCase()}
        </span>
      </button>
      <MobileRegionMenu
        handleAfterSubmitting={handleAfterSubmitting}
        localizeRegionLinks={localizeRegionLinks}
        localizedEntries={localizedEntries}
        packageObj={packageObj}
        sitesByContinent={sitesByContinent}
      />
      <nav
        aria-hidden={!isRegionMenuOpen}
        aria-label='Toast Region Switcher Menu'
        className={regionMenuClasses}
        id='nav-region-menu-desktop'
      >
        {Object.keys(sitesByContinent).map((continent) => {
          const localizedLinks = localizeRegionLinks({
            localizedEntries,
            packageObj,
            sites: sitesByContinent[continent]
          })
          return (
            <section
              className='pb-4 pr-4 mb-6 last:mb-0'
              key={`${continent}-desktop`}
            >
              <h5 className='font-effra font-normal leading-none text-secondary text-[15px] border-b border-gray-25 pb-2'>
                {t(continent)}
              </h5>
              <ul>
                {localizedLinks.map((regionLink: any) => (
                  <li className='mt-3' key={`${regionLink.language}-desktop`}>
                    <button
                      className='font-effra text-[15px] text-default font-normal hover:text-blue-75 hover:font-semibold'
                      onClick={() => handleAfterSubmitting(regionLink.url)}
                    >
                      {t(regionLink.language)}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )
        })}
      </nav>
    </div>
  ) : null
}

export default NavRegionSwitcher
